import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Lock, Star } from "lucide-react";
import { Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";
import { ikonaTechnologie, zkratkaTechnologie } from "../lib/technologie";

const tvar = (n, a, b, c) => (n === 1 ? a : n >= 2 && n <= 4 ? b : c);

function popisPouziti(p) {
  if (!p) return null;
  const casti = [
    p.technologie && `${p.technologie} ${tvar(p.technologie, "zakázka", "zakázky", "zakázek")}`,
    p.zarizeni && `${p.zarizeni} ${tvar(p.zarizeni, "zařízení", "zařízení", "zařízení")}`,
    p.polozky && `${p.polozky} ${tvar(p.polozky, "položka ceníku", "položky ceníku", "položek ceníku")}`,
    p.workflow && `${p.workflow} ${tvar(p.workflow, "úkol workflow", "úkoly workflow", "úkolů workflow")}`,
    p.specializace && `${p.specializace} ${tvar(p.specializace, "člověk ve specializaci", "lidé ve specializaci", "lidí ve specializaci")}`,
  ].filter(Boolean);
  return casti.length ? casti.join(" · ") : "zatím se nepoužívá";
}

/** Formulář pro novou i přejmenovanou technologii (inline pod seznamem / místo řádku). */
function Formular({ ikony, vychozi, onUlozit, onZrusit }) {
  const [name, setName] = useState(vychozi?.name || "");
  const [zkratky, setZkratky] = useState(vychozi?.zkratky || "");
  const [icon, setIcon] = useState(vychozi?.icon || "box");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const odeslat = async (e) => {
    e.preventDefault();
    setBusy(true); setChyba("");
    try { await onUlozit({ name: name.trim(), zkratky: zkratky.trim(), icon }); }
    catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); setBusy(false); }
  };
  return (
    <form onSubmit={odeslat} className="rise mt-3 p-4 rounded-[16px] bg-surface flex flex-col gap-3" data-testid="tech-form">
      <div className="grid grid-cols-[minmax(0,1fr)_96px] md:grid-cols-[minmax(0,1fr)_110px] gap-2.5">
        <label className="min-w-0">
          <span className="label-up text-muted block mb-1.5">Název</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="Kotel"
            className="w-full min-w-0 h-11 rounded-[12px] bg-white border border-line-strong px-3 text-[16px] md:text-[15px] text-ink outline-none focus:border-ink" data-testid="tech-form-nazev" />
        </label>
        <label className="min-w-0">
          <span className="label-up text-muted block mb-1.5">Zkratka</span>
          <input value={zkratky} onChange={(e) => setZkratky(e.target.value.toUpperCase())} maxLength={6}
            placeholder={(name || "KOT").slice(0, 3).toUpperCase()}
            className="w-full min-w-0 h-11 rounded-[12px] bg-white border border-line-strong px-3 text-[16px] md:text-[15px] font-700 text-ink outline-none focus:border-ink uppercase" data-testid="tech-form-zkratka" />
        </label>
      </div>
      <div>
        <span className="label-up text-muted block mb-1.5">Ikona</span>
        <div className="flex flex-wrap gap-1.5" data-testid="tech-form-ikony">
          {ikony.map((k) => {
            const Ikona = ikonaTechnologie(k);
            return (
              <button key={k} type="button" onClick={() => setIcon(k)} aria-pressed={icon === k} aria-label={k} data-testid={`tech-ikona-${k}`}
                className={`h-11 w-11 md:h-10 md:w-10 rounded-[12px] inline-flex items-center justify-center ${
                  icon === k ? "bg-ink text-white" : "bg-white text-sec hover:text-ink"}`}>
                <Ikona size={18} strokeWidth={1.9} />
              </button>
            );
          })}
        </div>
      </div>
      {chyba && <p className="t-meta text-danger" data-testid="tech-form-chyba">{chyba}</p>}
      <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
        <button type="button" onClick={onZrusit} className="tap md:min-h-0 h-10 px-4 rounded-full t-meta md:text-[13px] font-700 text-sec hover:text-ink">Zrušit</button>
        <button type="submit" disabled={busy || !name.trim()} data-testid="tech-form-ulozit"
          className="tap md:min-h-0 w-full md:w-auto h-11 md:h-10 px-5 rounded-full bg-accent text-white text-[14px] md:text-[13px] font-700 inline-flex items-center justify-center disabled:opacity-60">
          {busy ? <Spinner /> : vychozi ? "Uložit" : "Přidat technologii"}
        </button>
      </div>
    </form>
  );
}

/** Nastavení → Technologie: výchozí ProfitPilotu se zámkem + vlastní technologie firmy. */
export default function TechnologieNastaveni({ muzeUpravit }) {
  const [data, setData] = useState(null);       // {items, ikony}
  const [form, setForm] = useState(null);       // null | "nova" | id upravované
  const [potvrdit, setPotvrdit] = useState(null);
  const [barvaPro, setBarvaPro] = useState(null);   // id technologie, které se vybírá barva
  const [chyba, setChyba] = useState("");

  const load = useCallback(() => {
    api.get("/api/kategorie").then(({ data }) => setData(data)).catch(() => setData({ items: [], ikony: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Hvězdička = technologie předvybraná v okně „Přidat technologii“ (bez ní nejčastější).
  const predvybrat = async (k) => {
    const nova = data.predvybrana_id === k.id ? null : k.id;
    setData((d) => ({ ...d, predvybrana_id: nova }));
    try { await api.put("/api/kategorie/vychozi", { kategorie_id: nova }); }
    catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); load(); }
  };
  // Barva technologie v PDF nabídek (rozpočet, dlaždice, souhrn). Každá technologie má jinou.
  const nastavBarvu = async (k, barva) => {
    setChyba("");
    try {
      await api.put(`/api/kategorie/${k.id}/barva`, { barva });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === k.id ? { ...x, barva } : x)) }));
      setBarvaPro(null);
    } catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); }
  };
  const pridat = async (body) => { await api.post("/api/kategorie", body); setForm(null); load(); };
  const upravit = (id) => async (body) => { await api.patch(`/api/kategorie/${id}`, body); setForm(null); load(); };
  const smazat = async (k) => {
    if (potvrdit !== k.id) { setPotvrdit(k.id); return; }
    setChyba("");
    try { await api.delete(`/api/kategorie/${k.id}`); setPotvrdit(null); load(); }
    catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); setPotvrdit(null); }
  };

  return (
    <div className="card min-w-0" data-testid="nastaveni-technologie">
      <div className="flex items-center gap-2.5 px-4 md:px-5 pt-4 md:pt-[18px]">
        <h2 className="t-sekce font-700 text-ink">Technologie</h2>
        {muzeUpravit && form !== "nova" && (
          <button type="button" onClick={() => setForm("nova")} data-testid="tech-pridat"
            className="tap md:min-h-0 ml-auto inline-flex items-center gap-1 t-meta md:text-[13px] font-700 text-accent">
            <Plus size={15} strokeWidth={2.4} /> Přidat
          </button>
        )}
      </div>
      <div className="px-4 md:px-5 pt-2 pb-4 md:pb-[18px]">
        <p className="t-meta text-muted mb-1.5">
          Technologie, které montujete a servisujete. Podle nich se dělí obchodní případy, zařízení, ceník a specializace týmu.
          {muzeUpravit && <> Hvězdičkou vyberete tu, která se předvybere při přidání technologie k případu.
            Klepnutím na ikonu změníte barvu technologie v PDF nabídek.</>}
        </p>
        {data === null ? <div className="py-8 flex justify-center"><Spinner /></div> : data.items.map((k) => {
          const Ikona = ikonaTechnologie(k.icon);
          if (form === k.id) {
            return <Formular key={k.id} ikony={data.ikony} vychozi={k} onUlozit={upravit(k.id)} onZrusit={() => setForm(null)} />;
          }
          const pouzita = k.pouziti && Object.values(k.pouziti).some(Boolean);
          const obsazene = new Set(data.items.filter((x) => x.id !== k.id).map((x) => x.barva));
          return (
            <React.Fragment key={k.id}>
            <div className="flex items-center gap-3 py-3 md:px-1 border-t border-line-soft" data-testid={`tech-radek-${k.id}`}>
              <button type="button" disabled={!muzeUpravit} onClick={() => setBarvaPro(barvaPro === k.id ? null : k.id)}
                title={muzeUpravit ? "Barva v PDF nabídek — klepnutím změníte" : "Barva v PDF nabídek"} aria-label={`Barva technologie ${k.name}`}
                aria-expanded={barvaPro === k.id} data-testid={`tech-barva-${k.id}`}
                className="tap md:min-h-0 h-[38px] w-[38px] rounded-[12px] text-white inline-flex items-center justify-center shrink-0 disabled:cursor-default"
                style={{ background: k.barva || "#5c6169" }}>
                <Ikona size={18} strokeWidth={1.9} />
              </button>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="t-telo md:text-[14.5px] font-700 text-ink truncate">{k.name}</span>
                  <span className={`h-5 px-2 rounded-full text-[10px] font-700 tracking-[.06em] uppercase inline-flex items-center shrink-0 ${
                    k.vlastni ? "bg-[#fff1e8] text-accent" : "bg-surface text-muted"}`}>{k.vlastni ? "Vlastní" : "Výchozí"}</span>
                </span>
                {k.pouziti && <span className="block t-meta text-muted mt-0.5">{popisPouziti(k.pouziti)}</span>}
              </span>
              <span className="hidden sm:inline amount t-meta font-700 text-muted w-12 shrink-0">{zkratkaTechnologie(k)}</span>
              {muzeUpravit && (
                <button type="button" onClick={() => predvybrat(k)} aria-pressed={data.predvybrana_id === k.id}
                  title={data.predvybrana_id === k.id ? "Předvybraná při přidání technologie — klepnutím zrušíte" : "Předvybrat při přidání technologie"}
                  aria-label={`Předvybrat ${k.name}`} data-testid={`tech-predvybrat-${k.id}`}
                  className={`tap md:min-h-0 h-9 w-9 rounded-full inline-flex items-center justify-center shrink-0 hover:bg-surface ${
                    data.predvybrana_id === k.id ? "text-accent" : "text-line-strong hover:text-muted"}`}>
                  <Star size={16} strokeWidth={2} fill={data.predvybrana_id === k.id ? "currentColor" : "none"} />
                </button>
              )}
              {k.vlastni && muzeUpravit ? (
                <span className="inline-flex gap-1 shrink-0">
                  <button type="button" onClick={() => { setForm(k.id); setPotvrdit(null); }} title="Upravit" aria-label={`Upravit ${k.name}`}
                    className="tap md:min-h-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-sec hover:bg-surface" data-testid={`tech-upravit-${k.id}`}>
                    <Pencil size={15} strokeWidth={1.9} />
                  </button>
                  <button type="button" onClick={() => smazat(k)} onBlur={() => setPotvrdit(null)} disabled={pouzita}
                    title={pouzita ? "Používá se — smazat nejde" : "Smazat"} aria-label={`Smazat ${k.name}`} data-testid={`tech-smazat-${k.id}`}
                    className={`tap md:min-h-0 h-9 rounded-full inline-flex items-center justify-center gap-1 text-[12px] font-700 disabled:text-line-strong disabled:cursor-not-allowed ${
                      potvrdit === k.id ? "px-3 bg-danger text-white" : "w-9 text-danger hover:bg-surface"}`}>
                    <Trash2 size={15} strokeWidth={1.9} />{potvrdit === k.id && "Smazat?"}
                  </button>
                </span>
              ) : !k.vlastni ? (
                <span className="w-9 md:w-[76px] inline-flex justify-center text-line-strong shrink-0" title="Výchozí technologie ProfitPilotu — nejde měnit">
                  <Lock size={15} strokeWidth={2} />
                </span>
              ) : null}
            </div>
            {barvaPro === k.id && (
              <div className="rise pb-3 pl-[50px] flex flex-wrap gap-1.5" role="group" aria-label={`Barva ${k.name}`} data-testid={`tech-paleta-${k.id}`}>
                {(data.paleta || []).map((b) => (
                  <button key={b} type="button" disabled={obsazene.has(b)} onClick={() => nastavBarvu(k, b)}
                    aria-pressed={k.barva === b} aria-label={obsazene.has(b) ? `${b} — má jiná technologie` : b}
                    title={obsazene.has(b) ? `Má ${data.items.find((x) => x.barva === b && x.id !== k.id)?.name}` : "Použít"}
                    className={`tap md:min-h-0 md:min-w-0 h-9 w-9 md:h-8 md:w-8 rounded-full disabled:opacity-20 disabled:cursor-not-allowed ${
                      k.barva === b ? "ring-2 ring-offset-2 ring-ink" : ""}`}
                    style={{ background: b }} data-testid={`tech-paleta-${k.id}-${b.slice(1)}`} />
                ))}
              </div>
            )}
            </React.Fragment>
          );
        })}
        {chyba && <p className="t-meta text-danger mt-2" data-testid="tech-chyba">{chyba}</p>}
        {form === "nova" && data && <Formular ikony={data.ikony} onUlozit={pridat} onZrusit={() => setForm(null)} />}
      </div>
    </div>
  );
}
